import { cosineSimilarity, clamp } from './math';
import { computeSafetyRisk, computeSafetyAdjust } from './safety';

export const DEFAULT_INTEREST_WEIGHTS = {
  quality: 0.6,
  topic: 0.4
};

export const DEFAULT_DIVERSITY_PARAMS = {
  redundancyPenalty: 0.2,
  formatWeight: 0.15
};

export const DEFAULT_SCORE_WEIGHTS = {
  emotion: 0.5,
  interest: 0.35
};

export function computeEmotionMatch(userVector, contentVector) {
  return (cosineSimilarity(userVector, contentVector) + 1) / 2;
}

export function computeInterestScore({ quality = 0, topicSimilarity = 0 }, weights = DEFAULT_INTEREST_WEIGHTS) {
  return (
    clamp(quality, 0, 1) * (weights.quality ?? DEFAULT_INTEREST_WEIGHTS.quality) +
    clamp(topicSimilarity, 0, 1) * (weights.topic ?? DEFAULT_INTEREST_WEIGHTS.topic)
  );
}

export function computeDiversityAdjust({ redundancy = 0, formatFit = 0 }, params = DEFAULT_DIVERSITY_PARAMS) {
  const penalty = clamp(redundancy, 0, 1) * (params.redundancyPenalty ?? DEFAULT_DIVERSITY_PARAMS.redundancyPenalty);
  const reward = clamp(formatFit, 0, 1) * (params.formatWeight ?? DEFAULT_DIVERSITY_PARAMS.formatWeight);
  return reward - penalty;
}

export function computeBaseScore({
  userVector,
  contentVector,
  quality,
  topicSimilarity,
  redundancy,
  formatFit,
  weights = DEFAULT_SCORE_WEIGHTS,
  diversityParams = DEFAULT_DIVERSITY_PARAMS,
  interestWeights = DEFAULT_INTEREST_WEIGHTS
}) {
  const emotion = computeEmotionMatch(userVector, contentVector);
  const interest = computeInterestScore({ quality, topicSimilarity }, interestWeights);
  const diversity = computeDiversityAdjust({ redundancy, formatFit }, diversityParams);
  return (
    (weights.emotion ?? DEFAULT_SCORE_WEIGHTS.emotion) * emotion +
    (weights.interest ?? DEFAULT_SCORE_WEIGHTS.interest) * interest +
    diversity
  );
}

export function computeFinalScore({
  userVector,
  contentVector,
  quality = 0,
  topicSimilarity = 0,
  redundancy = 0,
  formatFit = 0,
  safety = {},
  weights,
  diversityParams,
  interestWeights,
  safetySettings
}) {
  const baseScore = computeBaseScore({
    userVector,
    contentVector,
    quality,
    topicSimilarity,
    redundancy,
    formatFit,
    weights,
    diversityParams,
    interestWeights
  });

  const safetyRisk = computeSafetyRisk(safety);
  const adjust = computeSafetyAdjust(safetyRisk, safetySettings);

  return {
    baseScore,
    safetyRisk,
    safetyAdjust: adjust,
    finalScore: baseScore * adjust
  };
}

export function buildRankingExplanation({
  emotionMatch,
  interest,
  diversityAdjust,
  safetyAdjust,
  prototypes
}) {
  const components = [];
  if (Number.isFinite(emotionMatch)) components.push(`Emotion match: ${(emotionMatch * 100).toFixed(0)}%`);
  if (Number.isFinite(interest)) components.push(`Interest: ${(interest * 100).toFixed(0)}%`);
  if (Number.isFinite(diversityAdjust)) components.push(`Diversity adjust: ${diversityAdjust.toFixed(2)}`);
  if (Number.isFinite(safetyAdjust)) components.push(`Safety factor: ${safetyAdjust.toFixed(2)}`);
  if (Array.isArray(prototypes) && prototypes.length) {
    components.push(`Emotion prototypes: ${prototypes.map((p) => `${(p.weight * 100).toFixed(0)}% ${p.label}`).join(', ')}`);
  }
  return components.join(' • ');
}

const rankingEngine = {
  computeEmotionMatch,
  computeInterestScore,
  computeDiversityAdjust,
  computeBaseScore,
  computeFinalScore,
  buildRankingExplanation
};

export default rankingEngine;
